import type { ReactNode } from 'react'
import { Alert, AlertTitle } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { hasEntity, type AppEntity } from '@mefosa/shared'
import { useAuth } from './AuthProvider'

/**
 * Which screen each route belongs to. Longest prefix wins.
 *
 * Keyed by path rather than by sidebar entry because the detail screens
 * (`/companies/:id`, `/deals/:id`, `/products/:id` …) appear in no menu and
 * would otherwise be unguarded — reachable by typing the URL, or by following
 * a link from a screen the person can still see.
 *
 * Every route registered in App.tsx must appear here. A test asserts that.
 */
export const ROUTE_ENTITIES: ReadonlyArray<readonly [string, AppEntity]> = [
  ['/dashboard', 'dashboard'],
  ['/today', 'today'],
  ['/activity', 'activity-log'],

  ['/companies', 'companies'],
  ['/contacts', 'contacts'],
  ['/whatsapp', 'whatsapp'],

  ['/staff', 'staff'],
  ['/leave', 'leave'],
  ['/timesheets', 'timesheets'],
  ['/expenses', 'expenses'],
  ['/payables', 'payables'],
  ['/purchases', 'purchases'],
  ['/deals', 'deals'],
  ['/import', 'import'],
  ['/duplicates', 'duplicates'],

  ['/audits', 'audits'],
  ['/findings', 'findings'],
  ['/certificates', 'certificates'],

  ['/courses', 'courses'],
  ['/sessions', 'sessions'],
  ['/trainees', 'trainees'],

  ['/lab', 'laboratory'],
  ['/products', 'inventory'],
  ['/suppliers', 'suppliers'],
  ['/projects', 'projects'],

  ['/quotations', 'quotations'],
  ['/invoices', 'invoices'],
  ['/credit-notes', 'creditNotes'],

  ['/reports', 'reports'],
  ['/team', 'team'],
  // The audit trail rides on the Team screen's grant: auditLog.read lives there.
  ['/audit-trail', 'team'],
]

export function entityForPath(pathname: string): AppEntity | null {
  return ROUTE_ENTITIES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )?.[1] ?? null
}

/**
 * Refuses a screen the person has not been given.
 *
 * This exists because the alternative is worse than an error. `ResourcePage`
 * skips its query when access is denied and renders its empty state, so
 * `/companies` would tell somebody without it that MEFOSA has no companies.
 * That is a lie, not a refusal.
 *
 * It also has to be keyed on the SCREEN rather than the permission. Several
 * screens legitimately borrow a sibling's permission — Non-conformities reads
 * audits for its required picker — so a permission check alone would let the
 * borrowed screen through as well.
 *
 * It says so rather than redirecting: a silent bounce to the dashboard reads
 * as a bug and leaves the person unable to say what they could not reach.
 */
export function ModuleGuard({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { pathname } = useLocation()

  const entity = entityForPath(pathname)
  if (user && entity && !hasEntity(user, entity)) {
    return (
      <Alert severity="info">
        <AlertTitle>{t('errors.noModuleAccessTitle')}</AlertTitle>
        {t('errors.noModuleAccess', { section: t(`entities.${entity}`) })}
      </Alert>
    )
  }

  return <>{children}</>
}
